/**
 * Para onde o "Voltar" da ficha leva, e como a lista de clientes sabe que a pessoa voltou.
 *
 * NO LEGADO o "Voltar" é `mostrar('tela-lista')` (`admin.html:6158` e `3332-3340`): a tela de
 * listas reaparece com a aba de topo que estava ativa (Clientes, Vendas ou Compromissos) e com a
 * lista como estava, SEM reler o banco. A lista de clientes só relê ao entrar no painel (`3515`), ao
 * clicar na aba de topo "Clientes" (`6449`) e depois de excluir (`4814`).
 *
 * AQUI a lista desmonta ao abrir a ficha, então as duas coisas precisam viajar na rota, que é
 * estado do React e não aparece na barra de endereço:
 *   · `de=<rota>` diz para onde voltar; sem ele, a lista de clientes;
 *   · `volta=1` diz à lista de clientes que ela está voltando de uma ficha, e que deve mostrar a
 *     lista guardada em vez de reler. O clique no menu leva a `/clientes` sem a marca, e relê, como
 *     o clique na aba de topo do legado.
 *
 * Funções puras: sem banco, sem tela e sem estado.
 */

// O endereço da ficha e o filtro da rota, os mesmos que o resto do painel usa.
import { filtroDaRota, rotaDaFicha } from '../../../rotas.js';

// A rota da lista de clientes, que é o destino padrão do "Voltar" (`admin.html:6158`).
export const ROTA_DA_LISTA = '/clientes';

// O nome da marca de volta na rota; um só lugar, para quem põe e quem lê não divergirem.
const MARCA_DE_VOLTA = 'volta';

// Separa a rota em caminho e filtro, como `AreaUtil` faz (`rotas.tsx`).
function partes(rota: string): { caminho: string; filtro: URLSearchParams } {
  // O caminho é o que vem antes do primeiro `?`; o resto é o filtro.
  const indice = rota.indexOf('?');
  return { caminho: indice === -1 ? rota : rota.slice(0, indice), filtro: filtroDaRota(rota) };
}

// Junta caminho e filtro de volta, sem `?` sobrando quando o filtro fica vazio.
function juntar(caminho: string, filtro: URLSearchParams): string {
  const texto = filtro.toString();
  return texto ? `${caminho}?${texto}` : caminho;
}

/** A rota sem a marca de volta: é o "onde a pessoa está" que a lista manda para a ficha. */
export function semMarcaDeVolta(rota: string): string {
  const { caminho, filtro } = partes(rota);
  filtro.delete(MARCA_DE_VOLTA);
  return juntar(caminho, filtro);
}

/** A lista de clientes está voltando de uma ficha. */
export function ehVoltaDaFicha(rota: string): boolean {
  return filtroDaRota(rota).get(MARCA_DE_VOLTA) === '1';
}

/**
 * A rota do "Voltar": a de origem (`de`), ou a lista de clientes. Quando o destino é a lista de
 * clientes, ele leva a marca de volta, para a lista aparecer como estava.
 */
export function destinoDoVoltar(de: string | null): string {
  // Sem origem, a lista de clientes, como o legado quando a ficha veio da lista.
  const { caminho, filtro } = partes(de || ROTA_DA_LISTA);
  // Outra área (Vendas, Compromissos): a rota dela, intocada.
  if (caminho !== ROTA_DA_LISTA) return juntar(caminho, filtro);
  filtro.set(MARCA_DE_VOLTA, '1');
  return juntar(caminho, filtro);
}

/**
 * O endereço da ficha com o atendimento e a origem.
 *
 * A ORIGEM ENTRA SEMPRE, inclusive quando é a lista de clientes pura, e isso não é enfeite: é por
 * ela que a ficha sabe se o lead foi lido com todas as colunas (Kanban e compromisso, `select('*')`
 * em `admin.html:6511` e `4540`) ou com as quatro da lista (`5878`). Ver `abriuPelaAreaDeClientes`.
 */
export function enderecoDaFicha(idCliente: string, opcoes: { readonly lead?: number | null; readonly de?: string }): string {
  const filtro = new URLSearchParams();
  // O lead escolhido, quando há um.
  if (opcoes.lead !== undefined && opcoes.lead !== null) filtro.set('lead', String(opcoes.lead));
  // A origem, sem a marca de volta que ela possa ter trazido.
  const de = opcoes.de ? semMarcaDeVolta(opcoes.de) : '';
  if (de) filtro.set('de', de);
  return juntar(rotaDaFicha(idCliente), filtro);
}

/**
 * A ficha foi aberta pela área Clientes (a lista, a escolha de atendimento ou o recorte do Painel).
 *
 * POR QUE A FICHA PRECISA SABER: no legado, quem abre pela lista passa um lead de quatro colunas
 * (`abrirClienteComEscolhaDeLead`, `admin.html:5877-5885`) e quem abre pelo Kanban ou pelo
 * compromisso passa a linha inteira (`6511` e `4540`). O Histórico escreve "Lead criada · origem: X"
 * só quando a origem existe no objeto (`5663-5666`), então a mesma cliente mostra a origem quando a
 * ficha vem do funil e não mostra quando vem da lista. Sem esta distinção, a origem apareceria nos
 * dois caminhos, e o Histórico deixaria de ser igual ao do admin atual.
 *
 * Sem `de` a resposta é "não": hoje quem chega assim é o Compromissos, que ainda não manda a origem
 * (pedido ao principal na paridade da A2), e o legado ali TEM a origem.
 */
export function abriuPelaAreaDeClientes(de: string | null): boolean {
  if (!de) return false;
  return partes(de).caminho === ROTA_DA_LISTA;
}
